import { DateTime, Interval } from 'luxon';
import { Interview, SingleDayOption } from '../../types';
import { getDateShort, getTimeCategory } from '../../utils/date-utils';

const createListOfDays = (interview: Interview | undefined): string[] => {
    if (!interview) return [];
    const { firstDate, lastDate } = interview.settings;

    const [firstDateAsLuxon, lastDateAsLuxon] = [
        DateTime.fromISO(firstDate).plus({ days: -2 }),
        DateTime.fromISO(lastDate).plus({ days: 2 }),
    ];
    const days = Interval.fromDateTimes(firstDateAsLuxon, lastDateAsLuxon)
        .splitBy({ day: 1 })
        .map((d) => getDateShort(d.start, 'en'));

    return days;
};

const diffAsString = (startTime: DateTime, endTime: DateTime): string => {
    const diff = endTime.diff(startTime, ['hours', 'minutes']).toObject();
    let durationTime = '';

    if (diff.hours) {
        durationTime = `${diff.hours}h`;
    }

    if (diff.minutes) {
        if (durationTime) {
            durationTime += ' ';
        }
        durationTime += `${diff.minutes}m`;
    }
    return durationTime;
};

const hasValidTimeSlot = (startTime: DateTime, endTime: DateTime, maximumDurationMin: number): boolean => {
    const diff = endTime.diff(startTime, ['minutes']).toObject();
    return diff.minutes === maximumDurationMin;
};

const createArrayOfSelectedTimes = (
    interview: Interview | undefined,
    timeZone: string,
): [string, SingleDayOption[]][] => {
    if (!interview) return [];
    const listOfDays = createListOfDays(interview);
    const { maximumDurationMin } = interview;

    const result: [string, SingleDayOption[]][] = listOfDays.map((day) => {
        const intervals = Interval.fromDateTimes(
            DateTime.fromFormat(day, 'M/d/y', { zone: timeZone }).startOf('day'),
            DateTime.fromFormat(day, 'M/d/y', { zone: timeZone }).endOf('day'),
        )
            .splitBy({ minutes: maximumDurationMin })
            .map((d) => ({
                duration: {
                    startTime: d.start.toUTC().toString(),
                    endTime: d.end.toUTC().toString(),
                    durationTime: diffAsString(d.start, d.end),
                },
                hash: String(d.start.toMillis() / 10000),
                isSuggested: true,
                timeCategory: getTimeCategory(d.start),
                isValid: hasValidTimeSlot(d.start, d.end, interview.maximumDurationMin),
            }))
            .filter((d) => d.isValid);

        return [day, intervals];
    });

    return result;
};

export { createArrayOfSelectedTimes, createListOfDays };
